/* Melora — product page interactivity for the Floward-style gallery overlay.
   Wishlist is a lightweight, client-side "save for this browser" list stored
   in localStorage (no account/back-end wishlist exists on this theme yet) —
   it is intentionally scoped to a visual save/unsave affordance only.
   Share uses the native Web Share sheet when available and falls back to
   copying the product link to the clipboard. */
(function () {
  "use strict";

  function getSaved() {
    try {
      return JSON.parse(localStorage.getItem("melora:wishlist") || "[]");
    } catch (e) {
      return [];
    }
  }

  function setSaved(list) {
    try {
      localStorage.setItem("melora:wishlist", JSON.stringify(list));
    } catch (e) {
      /* storage unavailable — fail silently, button still toggles visually */
    }
  }

  function initWishlistButtons(root) {
    var buttons = root.querySelectorAll("[data-wishlist-toggle]");
    buttons.forEach(function (btn) {
      var id = btn.getAttribute("data-product-id");
      var saved = getSaved();
      var isSaved = id && saved.indexOf(id) !== -1;
      btn.classList.toggle("is-active", isSaved);
      btn.setAttribute("aria-pressed", isSaved ? "true" : "false");

      btn.addEventListener("click", function () {
        if (!id) return;
        var list = getSaved();
        var idx = list.indexOf(id);
        var nowSaved;
        if (idx === -1) {
          list.push(id);
          nowSaved = true;
        } else {
          list.splice(idx, 1);
          nowSaved = false;
        }
        setSaved(list);
        btn.classList.toggle("is-active", nowSaved);
        btn.setAttribute("aria-pressed", nowSaved ? "true" : "false");
        btn.setAttribute(
          "aria-label",
          nowSaved ? "Remove from wishlist" : "Add to wishlist"
        );
      });
    });
  }

  function initShareButtons(root) {
    var buttons = root.querySelectorAll("[data-share-toggle]");
    buttons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var title = btn.getAttribute("data-share-title") || document.title;
        var url = btn.getAttribute("data-share-url") || window.location.href;

        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () {});
          return;
        }

        var restore = btn.getAttribute("aria-label");
        var done = function (label) {
          btn.classList.add("is-copied");
          btn.setAttribute("aria-label", label);
          setTimeout(function () {
            btn.classList.remove("is-copied");
            btn.setAttribute("aria-label", restore);
          }, 1800);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(url)
            .then(function () {
              done("Link copied");
            })
            .catch(function () {
              done("Copy failed");
            });
        }
      });
    });
  }

  /* The sticky Add to Cart bar (Muup's sticky-add-to-cart section) ships
     its own separate <form>, and in the "default-swatches" style it has no
     quantity field of its own — submitting it always adds qty 1, no matter
     what the visible quantity stepper on the page says. That's harmless
     while the inline Add to Cart button is also on screen (people use
     whichever one shows the right quantity), but on mobile the inline
     button is now hidden behind the sticky bar, so the sticky form has to
     carry the real quantity itself or "Add to Cart" silently ignores it. */
  function initStickyQuantitySync() {
    var mainQty = document.querySelector(".main-product .quantity__input");
    var stickyForm = document.querySelector(
      '.sticky-atc-bar form[data-type="add-to-cart-form"]'
    );
    if (!mainQty || !stickyForm) return;

    var hidden = stickyForm.querySelector('input[name="quantity"]');
    if (!hidden) {
      hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "quantity";
      stickyForm.appendChild(hidden);
    }

    var sync = function () {
      hidden.value = mainQty.value || "1";
    };
    sync();
    mainQty.addEventListener("change", sync);
  }

  /* Product details / Delivery info / Care tabs.
     The Liquid template already renders a run of collapsible_tab blocks as
     a native, mutually-exclusive accordion (details[name=...] — works with
     no JS at all). On desktop this progressively enhances that same markup
     into a horizontal tab bar with one shared content panel, matching the
     fnp.ae-style tab switcher. On mobile (below the theme's 750px
     breakpoint) the enhancement is skipped on purpose — a horizontal tab
     bar doesn't have room for 3 labels on a phone, so mobile keeps the
     native collapsible accordion instead. Resizing across the breakpoint
     (e.g. tablet rotation) builds or tears down the tab bar to match. If
     this never runs at all (JS disabled/blocked), the native accordion is
     the complete, fully-functional page — nothing here is required for
     the section to work. */
  var PRODUCT_TABS_MQ = "(min-width: 750px)";

  function buildProductTabs(container, accordions) {
    var tabs = accordions.map(function (acc, i) {
      var titleEl = acc.querySelector(".accordion__title");
      var contentEl = acc.querySelector(".accordion__content");
      return {
        label: titleEl ? titleEl.textContent.trim() : "Tab " + (i + 1),
        html: contentEl ? contentEl.innerHTML : "",
      };
    });

    var bar = document.createElement("div");
    bar.className = "product__tabs-bar";
    bar.setAttribute("role", "tablist");

    var panel = document.createElement("div");
    panel.className = "product__tabs-panel rte";
    panel.setAttribute("role", "tabpanel");

    /* Read more/less toggle for the panel — some tabs (Product Details on
       a long product) run to several paragraphs; clamped by default so a
       long tab doesn't push the rest of the page down, same "Read more"
       pattern already used for the mobile description. Hidden entirely
       when a tab's content is short enough to need no clamping. */
    var toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "product__tabs-toggle";
    toggle.setAttribute("aria-expanded", "false");
    toggle.innerHTML =
      '<span data-label-more>Read more</span><span data-label-less hidden>Read less</span>';

    function refreshToggle() {
      panel.classList.remove("is-expanded");
      toggle.classList.remove("is-visible");
      toggle.setAttribute("aria-expanded", "false");
      toggle.querySelector("[data-label-more]").hidden = false;
      toggle.querySelector("[data-label-less]").hidden = true;
      // Measure after layout settles — scrollHeight reflects the full
      // unclamped content even while max-height is clipping it visually.
      requestAnimationFrame(function () {
        var overflowing = panel.scrollHeight > panel.clientHeight + 2;
        toggle.classList.toggle("is-visible", overflowing);
      });
    }

    toggle.addEventListener("click", function () {
      var expanded = panel.classList.toggle("is-expanded");
      toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
      toggle.querySelector("[data-label-more]").hidden = expanded;
      toggle.querySelector("[data-label-less]").hidden = !expanded;
    });

    tabs.forEach(function (tab, i) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "product__tabs-btn" + (i === 0 ? " is-active" : "");
      btn.textContent = tab.label;
      btn.setAttribute("role", "tab");
      btn.setAttribute("aria-selected", i === 0 ? "true" : "false");
      btn.addEventListener("click", function () {
        Array.prototype.forEach.call(
          bar.querySelectorAll(".product__tabs-btn"),
          function (b) {
            b.classList.remove("is-active");
            b.setAttribute("aria-selected", "false");
          }
        );
        btn.classList.add("is-active");
        btn.setAttribute("aria-selected", "true");
        panel.innerHTML = tab.html;
        refreshToggle();
      });
      bar.appendChild(btn);
    });

    panel.innerHTML = tabs[0].html;

    container.insertBefore(panel, accordions[0]);
    container.insertBefore(bar, panel);
    container.insertBefore(toggle, panel.nextSibling);
    container.classList.add("js-tabs-active");
    refreshToggle();
  }

  function teardownProductTabs(container) {
    var bar = container.querySelector(".product__tabs-bar");
    var panel = container.querySelector(".product__tabs-panel");
    var toggle = container.querySelector(".product__tabs-toggle");
    if (bar) bar.remove();
    if (panel) panel.remove();
    if (toggle) toggle.remove();
    container.classList.remove("js-tabs-active");
  }

  function initProductTabs(root) {
    var containers = root.querySelectorAll(".product__tabs");
    var isDesktop = window.matchMedia(PRODUCT_TABS_MQ).matches;

    containers.forEach(function (container) {
      var accordions = Array.prototype.filter.call(
        container.children,
        function (el) {
          return el.classList.contains("product__accordion");
        }
      );
      if (accordions.length < 2) return;

      var isActive = container.classList.contains("js-tabs-active");
      if (isDesktop && !isActive) {
        buildProductTabs(container, accordions);
      } else if (!isDesktop && isActive) {
        teardownProductTabs(container);
      }
    });
  }

  function watchProductTabsBreakpoint(root) {
    var mq = window.matchMedia(PRODUCT_TABS_MQ);
    var onChange = function () {
      initProductTabs(root);
    };
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
    } else if (mq.addListener) {
      // Safari < 14
      mq.addListener(onChange);
    }
  }

  /* Product description "Read more" clamp — mobile only. On desktop the
     full description always shows (no clamp, no button). On mobile it's
     clamped to a few lines with a Read more / Read less toggle, but only
     when the text actually overflows that clamp — short descriptions never
     get a toggle they don't need. Re-measured on resize/orientation change
     since line-wrapping depends on actual width. */
  var DESCRIPTION_MOBILE_MQ = "(max-width: 749px)";

  function initDescriptionToggle(root) {
    var isMobile = window.matchMedia(DESCRIPTION_MOBILE_MQ).matches;
    var wraps = root.querySelectorAll(".product__description-wrap");

    wraps.forEach(function (wrap) {
      var desc = wrap.querySelector("[data-product-description]");
      var toggle = wrap.querySelector("[data-product-description-toggle]");
      if (!desc || !toggle) return;

      if (!isMobile) {
        toggle.hidden = true;
        desc.classList.remove("is-expanded");
        return;
      }

      desc.classList.remove("is-expanded");
      var overflowing = desc.scrollHeight > desc.clientHeight + 2;
      toggle.hidden = !overflowing;
      toggle.setAttribute("aria-expanded", "false");

      if (!toggle.dataset.bound) {
        toggle.dataset.bound = "true";
        var moreLabel = toggle.querySelector("[data-label-more]");
        var lessLabel = toggle.querySelector("[data-label-less]");
        toggle.addEventListener("click", function () {
          var expanded = desc.classList.toggle("is-expanded");
          if (moreLabel) moreLabel.hidden = expanded;
          if (lessLabel) lessLabel.hidden = !expanded;
          toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
        });
      }
    });
  }

  function watchDescriptionBreakpoint(root) {
    var debounced;
    window.addEventListener("resize", function () {
      clearTimeout(debounced);
      debounced = setTimeout(function () {
        initDescriptionToggle(root);
      }, 150);
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    initWishlistButtons(document);
    initShareButtons(document);
    initStickyQuantitySync();
    initProductTabs(document);
    watchProductTabsBreakpoint(document);
    initDescriptionToggle(document);
    watchDescriptionBreakpoint(document);
  });
})();
